'use client';

import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { setCookie } from 'cookies-next';
import { signIn } from 'next-auth/react';
import { Input, Button } from '@components/common/Forms';
import { PhoneNumberGroup } from '@components/features/modals/phone/partials/PhoneNumberGroup';
import { useSwrStaticPhoneCountries } from '@hooks/useSwrApi/endpoints/static';
import { useSwrSendPhoneApplyCode, useSwrVerifyPhoneApply } from '@hooks/useSwrApi/endpoints/phone-apply';
import { JOB_APPLY_COOKIE_NAME } from 'src/constants';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

interface PhoneApplyHubProps {
  locale: Locale;
  jobSlug: string;
  /** Called once a successful verify+session-swap navigates away, so the modal can close. */
  onSuccess?: () => void;
}

/**
 * @description Two-step inline flow for the guest phone-apply path.
 * Step 1: phone number + send-code submit.
 * Step 2: SMS code + verify+apply submit → on success:
 *   - drops JOB_APPLY_COOKIE_NAME (the same cookie the existing
 *     applyToJob() guest path uses post-login),
 *   - signs the user in via NextAuth `signIn('phone-otp')`,
 *   - hard-navigates to /job/{slug}/after-apply.
 *
 * The middleware (`proxy.ts`) picks up the cookie + auth session and
 * fires the actual `/v1/job/{slug}/apply` on the user's behalf,
 * routing to either the after-apply page (success) or back to the job
 * page with `?personalDataModal` (missing-profile gate) — the same UX
 * an email-registered first-time applicant gets today.
 *
 * See docs/plans/phone-apply-flow.md.
 */
export const PhoneApplyHub = (props: PhoneApplyHubProps) => {
  const { locale, jobSlug, onSuccess } = props;
  const { t } = useTranslation('common');

  const { data: phonePrefixes } = useSwrStaticPhoneCountries(locale);

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    phonePrefixes?.find((item) => item.id === 'RO')?.name || '40',
  );
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneValidityStates, setPhoneValidityStates] = useState({ isInvalid: false, errorMSG: '' });
  const [confirmSmsCode, setConfirmSmsCode] = useState<string>('');
  const [smsCodeValidityStates, setSmsCodeValidityStates] = useState({ isInvalid: false, errorMSG: '' });
  const [requestId, setRequestId] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);

  const mergedPhoneNumber = useMemo(
    () => `+${selectedCountryCode}${phoneNumber}`,
    [selectedCountryCode, phoneNumber],
  );

  const { trigger: triggerSendCode, isMutating: isSendingCode } = useSwrSendPhoneApplyCode({
    locale,
    jobSlug,
    onSuccess: (data) => {
      // Backend always returns requestId on success — the OpenAPI
      // schema marks it optional because that's the default for
      // response properties. Defensive coalesce anyway.
      setRequestId(data.requestId ?? null);
    },
  });

  const { trigger: triggerVerify, isMutating: isVerifying } = useSwrVerifyPhoneApply({
    locale,
    jobSlug,
    onSuccess: async (data) => {
      if (!data.oneTimeAuthToken) {
        setAuthError(t('phone_apply.error.generic', 'Something went wrong. Please try again.'));
        return;
      }

      setIsSwapping(true);

      // Drop the same cookie the existing applyToJob() guest path uses
      // after login redirect. The auto-apply middleware (proxy.ts)
      // reads this once we have a session and fires /v1/job/{slug}/apply
      // — exactly what an email-registered guest gets routed through.
      void setCookie(JOB_APPLY_COOKIE_NAME, JSON.stringify({ jobSlug }));

      // Client-side signIn (rather than the phoneOtpAction server
      // action) so we don't transitively pull baseAuth → next/headers
      // into the JobActions bundle — that page is still rendered via
      // the Pages Router. NextAuth still invokes phoneOtpCallback
      // server-side to swap the token for an API session.
      const result = await signIn('phone-otp', {
        token: data.oneTimeAuthToken,
        locale,
        redirect: false,
      });

      if (!result?.error) {
        onSuccess?.();
        // Navigate to after-apply; the middleware will intercept,
        // consume the cookie, fire the apply, and redirect to either
        // after-apply (success) or /job/{slug}?personalDataModal
        // (missing-profile gate).
        window.location.href = `/job/${jobSlug}/after-apply`;
        return;
      }

      setIsSwapping(false);
      setAuthError(t('phone_apply.error.generic', 'Something went wrong. Please try again.'));
    },
  });

  const sendCode = () => {
    void triggerSendCode({ phoneNumber: { number: mergedPhoneNumber } });
  };

  const verifyAndApply = () => {
    if (!requestId) return;
    setAuthError(null);
    void triggerVerify({
      phoneNumber: { number: mergedPhoneNumber },
      code: confirmSmsCode,
      requestId,
    });
  };

  const showCodeStep = requestId !== null;
  const isBusy = isSendingCode || isVerifying || isSwapping;

  return (
    <>
      {!showCodeStep && (
        <>
          <div className="bg-surface-100 p-6">
            <PhoneNumberGroup
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              phonePrefixes={phonePrefixes}
              selectedCountryCode={selectedCountryCode}
              setSelectedCountryCode={setSelectedCountryCode}
              phoneValidityStates={phoneValidityStates}
              setPhoneValidityStates={setPhoneValidityStates}
            />
            <p className="mt-4 text-sm text-ink-medium">
              {t('phone_apply.modal.description', 'We will text a confirmation code to this number to register your application.')}
            </p>
          </div>

          <div className="border-t bg-surface px-6 py-4 text-right">
            <Button
              type="button"
              color="secondary"
              size="sm"
              rounding="full"
              className="w-full sm:w-auto sm:!px-7"
              disabled={phoneNumber.length === 0 || phoneValidityStates.isInvalid}
              isLoading={isSendingCode}
              onClick={sendCode}
            >
              {t('phone_apply.modal.send_code', 'Send code')}
            </Button>
          </div>
        </>
      )}

      {showCodeStep && (
        <>
          <div className="bg-surface-100 p-6">
            <p>{t('account.confirm.phone.code.info')}</p>
            <div className="mt-4">
              <Input
                required
                type="tel"
                minLength={4}
                maxLength={4}
                value={confirmSmsCode}
                pattern="^\d{4}$"
                patternMismatchMessage={t('sms.confirm.input.pattern')}
                getValidityStates={setSmsCodeValidityStates}
                onChange={(e) => setConfirmSmsCode(e.target.value)}
              />
            </div>

            {authError && (
              <div className="mt-4 flex items-start gap-1.5 rounded bg-error/10 p-2 text-sm">
                <ExclamationTriangleIcon className="size-5 shrink-0 text-error" />
                <span>{authError}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 border-t bg-surface px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center text-sm sm:text-left">
              {t('account.settings.label.no_sms_received')}
              <Button
                type="button"
                styling="text"
                size="sm"
                rounding="full"
                className="ml-1"
                disabled={isBusy}
                onClick={sendCode}
              >
                {t('account.settings.label.resend_sms')}
              </Button>
            </div>

            <Button
              type="button"
              color="secondary"
              size="sm"
              rounding="full"
              className="w-full sm:w-auto sm:!px-7"
              disabled={smsCodeValidityStates.isInvalid || confirmSmsCode.length !== 4}
              isLoading={isVerifying || isSwapping}
              onClick={verifyAndApply}
            >
              {t('phone_apply.modal.verify_and_apply', 'Verify & Apply')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};
